import {getModule, Mutation, Action, Module, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import cart from '@/store/modules/cart';
import to from 'await-to-js';
import {applyGiftCard, getGiftCardCredit, removeGiftCard} from '@/store/api';
import checkout from '@/store/modules/checkout';
import {i18n} from '@/i18n';
import handleMessage from '@/commons/messageManager';
import {initState} from '../stateinit';
import {GiftCard, Totals} from '../models';

@Module({
    namespaced: true,
    name: 'giftcard',
    store,
    dynamic: true,
})
class GiftCardModule extends VuexModule {
    public giftCard: GiftCard = initState('giftCard');

    @Mutation
    public setGiftCardCode(code: string) {
        this.giftCard.code = code;
    }

    @Mutation
    public setGiftCard(giftCard: GiftCard) {
        this.giftCard = giftCard;
    }

    @Mutation
    public setVerified(value: boolean) {
        this.giftCard.verified = value;
    }

    @Mutation
    public setApplied(value: boolean) {
        this.giftCard.applied = value;
    }

    get verified() {
        return this.giftCard.verified;
    }

    get applied() {
        return this.giftCard.applied;
    }

    get giftcardSegment() {
        return cart.totals.total_segments.find((total) => {
            return total.code === 'giftcardaccount';
        });
    }

    @Action({rawError: true})
    public async getGiftCardCredit(giftCardCode: string): Promise<{error: string; credit: number}> {
        const {giftCard} = this;
        try {
            const credit = await getGiftCardCredit(giftCardCode);
            if (credit !== 0) {
                giftCard.code = giftCardCode;
                giftCard.credit = credit;
                this.setGiftCard(giftCard);

                return {error: '', credit};
            }
        } catch (error) {
            const {message} = error.response.data;
            return {
                error: message,
                credit: 0,
            };
        }
        return {
            error: 'The gift card is invalid or empty. Please check the gift card and try again',
            credit: 0,
        };
    }

    @Action({rawError: true})
    public async applyGiftCard(amount: any) {
        let applied: boolean = false;
        const {giftCard} = this;

        let applyRes = false;
        try {
            applyRes = await applyGiftCard(giftCard.code, amount);
        } catch (error) {
            const {message} = error.response.data;
            const {parameters} = error.response.data;
            let finalMessage = message;

            parameters.forEach((param: string, i: number) => {
                finalMessage = finalMessage.replace(`%${i + 1}`, param);
            });

            handleMessage('error', finalMessage);
        }

        applied = applyRes;
        giftCard.applied = applied;

        this.setGiftCard(giftCard);
        await cart.getTotals();
        await checkout.estimateShipping(checkout.shippingAddress);
        await checkout.getPaymentMethods(
            checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
            checkout.shippingAddress
        );

        return applied;
    }

    @Action({rawError: true})
    public async removeGiftCard() {
        let applied: boolean = false;
        const {giftCard} = this;

        const [, applyRes] = await to(removeGiftCard(giftCard.code));

        if (!applyRes) {
            applied = giftCard.applied;
        }

        giftCard.applied = false;

        this.setGiftCard(giftCard);
        await cart.getTotals();
        await checkout.estimateShipping(checkout.shippingAddress);
        await checkout.getPaymentMethods(
            checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
            checkout.shippingAddress
        );

        return applied;
    }

    @Action({rawError: true})
    public async changeGiftCardAppliedBasedOnTotals(totals: Totals) {
        const giftCardTotalSegment = totals.total_segments.find((total) => {
            return total.code === 'giftcardaccount';
        });

        if (this.giftCard.applied && giftCardTotalSegment === undefined) {
            this.setApplied(false);
            this.setVerified(false);
            handleMessage('error', i18n.t('Gift card was removed because the total amount is too low').toString());
        }
    }
}

export default getModule(GiftCardModule);
